import { useEffect, useRef, useState } from "react";
import * as signalR from "@microsoft/signalr";
import { DineInEventName, IDineInEventPayload } from "../models/IDineIn";
import { ensureFreshToken } from "../../services/tokenRefresh";
import { getUserToken } from "../../utils/Util";

const API_BASE = process.env.REACT_APP_API_URL ?? "https://service.mutfacikservices.com/api/";
/** /api/ → /hubs/dinein */
const HUB_URL = API_BASE.replace(/\/api\/?$/, "") + "/hubs/dinein";

const EVENTS: DineInEventName[] = [
  "DineInSessionOpened",
  "DineInOrderLinesAdded",
  "DineInOrderLineUpdated",
  "DineInOrderLineRemoved",
  "DineInSessionPaid",
  "DineInTableReleased",
  // 17 Eki: satır durumu (Çıktı / Teslim edildi). Listede olmazsa olay sessizce düşer.
  "DineInOrderLineStatusChanged",
];

/** Vazgeçmeden dene: 0 sn, 2 sn, 5 sn, 10 sn, sonra 30 sn'de bir. */
const RETRY_DELAYS = [0, 2000, 5000, 10000, 30000];
const retryDelay = (attempt: number) => RETRY_DELAYS[Math.min(attempt, RETRY_DELAYS.length - 1)];

export type DineInHubState = "idle" | "connecting" | "connected" | "unauthorized" | "error";

interface Options {
  token: string | null;
  restaurantId: string | null;
  /** Her olayda çağrılır; olay adı + yük. */
  onEvent?: (eventName: DineInEventName, payload: IDineInEventPayload) => void;
  /** Bağlantı koptuktan sonra yeniden kurulunca — kopuklukta kaçan olaylar için /floor tazelenmeli. */
  onResync?: () => void;
}

/**
 * Salon olayları ayrı hub'dan gelir (/hubs/dinein). Olaylar tek doğruluk kaynağı değildir —
 * ekran açılışta /floor çağırmalı, olayları yalnız tazeleme için kullanmalıdır.
 *
 * Garson tableti gün boyu açık kalır: token (60 dk) URL'ye bir kez gömülürse ilk kopuştan
 * sonra yeniden bağlanma 401 alıp sessizce ölürdü. Her bağlanmada güncel token okunur,
 * denemeler bitmez ve kapanan bağlantı baştan kurulur.
 */
export function useDineInHub({ token, restaurantId, onEvent, onResync }: Options) {
  const [state, setState] = useState<DineInHubState>("idle");
  const onEventRef = useRef(onEvent);
  const onResyncRef = useRef(onResync);
  useEffect(() => { onEventRef.current = onEvent; }, [onEvent]);
  useEffect(() => { onResyncRef.current = onResync; }, [onResync]);

  const loggedIn = !!token;

  useEffect(() => {
    if (!loggedIn || !restaurantId) { setState("idle"); return; }

    let disposed = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let hasConnectedOnce = false;
    let denied = false;

    const connection = new signalR.HubConnectionBuilder()
      // Long-polling'e düşmesin: restoranın bütün tabletleri aynı IP'den çıkıyor ve
      // WAF'ın istek sınırına takılabilir. Yalnızca WebSocket'e izin veriyoruz.
      .withUrl(HUB_URL, {
        transport: signalR.HttpTransportType.WebSockets,
        accessTokenFactory: async () => {
          await ensureFreshToken();
          return getUserToken()?.token ?? "";
        },
      })
      .withAutomaticReconnect({ nextRetryDelayInMilliseconds: (ctx) => retryDelay(ctx.previousRetryCount) })
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    EVENTS.forEach((name) => {
      connection.on(name, (payload: IDineInEventPayload) => {
        onEventRef.current?.(name, payload);
      });
    });

    const join = async () => {
      try {
        await connection.invoke("JoinRestaurantFloor", restaurantId);
        denied = false;
        if (!disposed) setState("connected");
        return true;
      } catch {
        // Sunucu rol + restoran sahipliğini doğrular; tek bir mesajla reddeder.
        // Kullanıcıya sunucunun İngilizce metnini değil kendi metnimizi gösteriyoruz.
        denied = true;
        if (!disposed) setState("unauthorized");
        return false;
      }
    };

    const start = async (attempt: number) => {
      if (disposed) return;
      setState("connecting");
      try {
        await connection.start();
        if (disposed) return;
        const joined = await join();
        if (joined && hasConnectedOnce) onResyncRef.current?.();
        hasConnectedOnce = true;
      } catch {
        if (disposed) return;
        setState("error");
        retryTimer = setTimeout(() => start(attempt + 1), retryDelay(attempt + 1));
      }
    };

    connection.onreconnecting(() => { if (!disposed) setState("connecting"); });
    connection.onreconnected(async () => {
      if (await join()) onResyncRef.current?.();
    });
    // Otomatik yeniden bağlanma da biterse (ya da sunucu kapatırsa) baştan kur
    connection.onclose(() => {
      if (disposed) return;
      setState("error");
      retryTimer = setTimeout(() => start(1), retryDelay(1));
    });

    // Tablet uykudan / sekme arka plandan dönünce ölü bağlantıyı beklemeden yeniden kur
    const onVisible = () => {
      if (document.visibilityState !== "visible" || disposed) return;
      if (connection.state === signalR.HubConnectionState.Disconnected) {
        clearTimeout(retryTimer);
        start(0);
      } else if (connection.state === signalR.HubConnectionState.Connected && denied) {
        join();
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    start(0);

    return () => {
      disposed = true;
      clearTimeout(retryTimer);
      document.removeEventListener("visibilitychange", onVisible);
      EVENTS.forEach((name) => connection.off(name));
      if (connection.state === signalR.HubConnectionState.Connected) {
        connection.invoke("LeaveRestaurantFloor", restaurantId).catch(() => {});
      }
      connection.stop().catch(() => {});
    };
  }, [loggedIn, restaurantId]);

  return state;
}
